import { Component, createRef } from 'preact';

import {
  beginRoll, makeDie, restImmediately, stepDie,
} from '../../utils/game/dice3d/animate';
import {
  d20Geometry, faceIndexForValue, restingQuaternion,
} from '../../utils/game/dice3d/geometry';
import { DiceGL, loadDiceArtwork } from '../../utils/game/dice3d/diceRenderer';
import { bank, preloadDiceAudio, rattle } from '../../utils/game/dice3d/diceAudio';

/**
 * One die on one tray. And **the tray is the whole card it sits in**.
 *
 * The canvas covers its parent edge to edge, so the die shoots off its slot in
 * the middle, ricochets off all four sides of the card. Banging wood and
 * spraying sparks at every wall. Runs over the name and over the result chip,
 * and comes back to the exact slot it started from. See
 * `utils/game/dice3d/animate.js` for why the homecoming is arithmetic rather
 * than a correction.
 *
 * The die rests at the tray's centre, which is why the card puts equal-height
 * blocks above and below the dice band: world origin is the middle of the
 * canvas, and that is where the ornament and the dish are centred too.
 *
 * The wheel (`rollStart` / `rollEnd`) is **not** driven here. It is one sound
 * for the whole roll-off, and with a tray per seat no single tray knows when
 * the last die has landed. So the modal owns it and reads `flightRemaining()`.
 *
 * Props:
 *   viewHalfWidth : half the world width the camera frames. Larger means a
 *                   smaller die on a wider tray.
 *   radius        : die radius in world units.
 *   palette       : DICE_PALETTES id the die wears (its seat's choice), or
 *                   null for the player's own pick.
 *   reducedMotion : land instantly instead of throwing.
 *
 * Ref API:
 *   .roll(value, crit)    tumble onto `value`. `crit` is 'high' | 'low' | null
 *                         and drives the landing firework. It has to be known
 *                         before touchdown, not after. Resolves once settled.
 *   .setDim(dim)          mute the die (the loser, after the roll-off).
 *   .flightRemaining()    seconds of flight left, 0 when resting.
 */

/**
 * Seconds after the die rests before the render loop parks itself. It has to
 * outlast the longest spark of the natural-20 firework, or the best roll
 * freezes mid-tinder.
 */
const AFTERGLOW = 1.45;
/** How long the settle gleam takes to reach full strength. */
const EMPHASIS_RAMP = 0.28;

export default class InitiativeDiceStage extends Component {
  holderRef = createRef();

  componentDidMount() {
    const holder = this.holderRef.current;
    if (!holder) return;

    this.canvas = document.createElement('canvas');
    this.canvas.style.width = '100%';
    this.canvas.style.height = '100%';
    this.canvas.style.display = 'block';
    holder.appendChild(this.canvas);

    try {
      this.gl = new DiceGL(this.canvas, this.props.viewHalfWidth);
    } catch (err) {
      // No WebGL (headless tests, blocked context): the card still works, it
      // just has no die on screen. Rolls resolve immediately.
      console.warn('[InitiativeDiceStage] WebGL unavailable:', err);
      return;
    }

    this.crit = null;
    this.gl.setPlan(1, this.props.palette ?? null);
    this.measure();
    this.die = makeDie(Math.random, [0, 0], this.props.radius ?? 0.92, this.gl.stage());
    this.paint();

    // Fetch and decode the foley now. Left to the first sound, decoding would
    // still be running when the wheel is scheduled and the first throw of a
    // session would run silent.
    preloadDiceAudio();

    window.addEventListener('resize', this.measure);
    if (typeof ResizeObserver !== 'undefined') {
      this.ro = new ResizeObserver(this.measure);
      this.ro.observe(this.canvas);
    }

    // The artwork arrives late: until then the die is blank card stock.
    loadDiceArtwork().then(() => {
      if (this._unmounted || !this.gl) return;
      this.gl.refreshArtwork();
      if (!this.raf) this.paint();
    });
  }

  componentDidUpdate(prev) {
    // The seat's palette arrives with the mirror, often after the tray is up.
    if (prev.palette !== this.props.palette && this.gl) {
      this.gl.setPlan(1, this.props.palette ?? null);
      if (!this.raf) this.paint();
    }
  }

  componentWillUnmount() {
    this._unmounted = true;
    if (this.raf) cancelAnimationFrame(this.raf);
    window.removeEventListener('resize', this.measure);
    if (this.ro) this.ro.disconnect();
    // Nobody is left to await a dead canvas.
    if (this.resolve) this.resolve();
    if (this.gl) this.gl.dispose();
    if (this.canvas?.parentNode) this.canvas.parentNode.removeChild(this.canvas);
  }

  measure = () => {
    if (!this.gl || !this.canvas) return;
    const rect = this.canvas.getBoundingClientRect();
    const width = Math.max(1, Math.round(rect.width || this.canvas.clientWidth || 200));
    const height = Math.max(1, Math.round(rect.height || this.canvas.clientHeight || 260));
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    this.gl.setSize(width, height, dpr);

    // With the frame the walls move. A resting die gets the new tray; rebuilding
    // the path mid-flight would be a jump, so a flying die keeps the tray it was
    // thrown into.
    if (this.die?.phase === 'rest') this.die.stage = this.gl.stage();
    if (!this.raf) this.paint();
  };

  paint() {
    if (!this.gl || !this.die) return;
    const emphasis = this.die.phase === 'rest'
      ? Math.min(1, this.die.restFor / EMPHASIS_RAMP)
      : 0;
    this.gl.render([this.die], emphasis, [this.crit]);
  }

  /** One frame further. Returns whether there is still anything to do. */
  advance(dt) {
    const die = this.die;
    const wasFlying = die.phase !== 'rest';
    stepDie(die, dt, Math.random);

    // **Two things make a sound, not three.** The cushion bangs where it was
    // struck (the hit's x maps onto the ears), and the arrival rattles. The
    // bounce on the table stays mute: while the die flies, the field belongs to
    // the wheel.
    const hit = die.impact;
    if (hit !== null && wasFlying) {
      if (hit.kind === 'wall') {
        bank(hit.strength, Math.max(-1, Math.min(1, hit.at[0] / this.gl.stage()[0])));
      } else if (hit.kind === 'settle') {
        rattle();
      }
    }

    if (wasFlying && die.phase === 'rest') {
      const resolve = this.resolve;
      this.resolve = null;
      if (resolve) resolve();
    }
    return die.phase !== 'rest' || die.restFor < AFTERGLOW;
  }

  startLoop() {
    if (this.raf) return;
    let last = 0;
    const tick = (now) => {
      if (this._unmounted) return;
      const previous = last;
      last = now;
      // The first step is zero: between requesting and the first frame lies the
      // whole setup, and the die would jump a stretch.
      const dt = previous === 0 ? 0 : Math.min(0.04, (now - previous) / 1000);
      const more = this.advance(dt);
      this.paint();
      this.raf = more ? requestAnimationFrame(tick) : null;
    };
    this.raf = requestAnimationFrame(tick);
  }

  /** Public. Seconds of flight left. The modal sizes the wheel from this. */
  flightRemaining() {
    const die = this.die;
    if (!die || die.phase === 'rest') return 0;
    return Math.max(0, die.delay + die.tour.duration - die.t);
  }

  /** Public. Tumble onto `value`. */
  roll(value, crit = null) {
    return new Promise((resolve) => {
      if (!this.die) {
        resolve();
        return;
      }
      const geometry = d20Geometry();
      const target = restingQuaternion(geometry, faceIndexForValue(geometry, value));
      const previous = this.resolve;
      this.resolve = resolve;
      this.crit = crit;
      if (previous) previous();

      if (this.props.reducedMotion) {
        restImmediately(this.die, target);
        this.resolve = null;
        this.paint();
        // Reduced motion is not a silent throw: what lies there has rattled.
        rattle();
        resolve();
        return;
      }

      beginRoll(this.die, target, 0, Math.random);
      this.startLoop();
    });
  }

  /** Public. Mute the die once the roll-off has resolved. */
  setDim(dim) {
    if (!this.gl) return;
    this.gl.setDim(0, dim);
    if (!this.raf) this.paint();
  }

  render() {
    return (
      <div
        ref={this.holderRef}
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          // Above the card's content: the die belongs on the table, not in a
          // compartment. It runs over the name, over the chip, and right up to
          // the card's edge.
          zIndex: 2,
        }}
      />
    );
  }
}
